import React, { useEffect, useState } from 'react';
import DataPipelineSmallIcon from '../../assets/DataPipeline_small.svg';
import { Box, Divider, Grid, Switch, Typography, useTheme } from '@mui/material';
import PageHeader from 'components/Header/Page';
import MultiLineWidget from 'components/Widgets/MultiLineChart';
import IncidentFilter from 'components/Dropdown/IncidentFilter';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import LoaderComponent from 'components/Loader';
import Insights from './Insights';
import { addOrUpdateInsights, deleteInsights, fetchIncidentTrend, fetchMetricsTechnologyList, fetchTechnologyDataList, getDataEstateSector, getInsights } from 'service/service';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import moment from 'moment';
import dayjs from 'dayjs';
import CustomDropdown from 'components/Dropdown';
import SectorFilter from 'components/Dropdown/SectorFilter';
import AIIcon from '../../assets/simple-icons_aiops.svg';
import MLIcon from '../../assets/simple-icons_aiops.svg';
import ProgramIcon from '../../assets/program.svg';
import BIOpsIcon from '../../assets/BIOps.svg';
import DepartmentIcon from '../../assets/DataPipeline_small.svg';
import PlatformCostLogo from '../../assets/ri_money-dollar-circle-fill.svg';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import NumberWidget from 'components/Widgets/NumberWidget';
import YearMonthPicker from 'components/Dropdown/YearMonthPicker';
import { SECTOR_OPTIONS } from 'utils/xopsUtil';
import { useLocation } from 'react-router-dom';
import CaptureExportControls from 'utils/CaptureExportControls';

// Types for the API data
interface IncidentData {
    yearMonth: string;
    incidentCount: number;
}

interface IncidentAPIResponse {
    incidents: IncidentData[];
    avg2024Incidents: IncidentData[];
}

// MODIFIED: Updated interface to include all possible fields from the API and our feature
interface Row {
    id: string;
    platform: string;
    technology: string;
    assignmentGroupCount: number | null;
    incidentVolume: number | null;
    automatedIncidentVolume?: number | null;
    availability: string | null;
    changeRequest: number | null;
    serviceRequest: number | null;
    mttr: number | null;
    responseSLA?: number | null;
    resolutionSLA?: number | null;
    incidentAging?: number | null;
    srAging?: number | null;
    srVolume?: number | null;
    agedSRs?: number | null;
    overallHappinessIndex?: number | null;
    happinessIndexIncident?: number | null;
    lostTimeIncident?: number | null;
    happinessIndexCR?: number | null;
    lostTimeCR?: number | null;
    crVolume?: number | null;
    agedCRs?: number | null;
    problemsVolume?: number | null;
    openProblems?: number | null;
    agedProblems?: number | null;
    costManagementTickets?: number | null;
    technologies?: any;
    userIncidentsL0?: number;
    userIncidentsL1?: number;
    userIncidentsL2?: number;
    userIncidentsL3?: number;
    automatedIncidentsL0?: number;
    automatedIncidentsL1?: number;
    automatedIncidentsL2?: number;
    automatedIncidentsL3?: number;
    userIncidentsL0Per?: number;
    userIncidentsL1Per?: number;
    userIncidentsL2Per?: number;
    userIncidentsL3Per?: number;
    automatedIncidentsL0Per?: number;
    automatedIncidentsL1Per?: number;
    automatedIncidentsL2Per?: number;
    automatedIncidentsL3Per?: number;


    l0Count?: number;
    l1Count?: number;
    l2Count?: number;
    l3Count?: number;
    l0CountPer?: number;
    l1CountPer?: number;
    l2CountPer?: number;
    l3CountPer?: number;

    isIncidentExpanded?: boolean;
    incidentChildren?: any[];
    isChildren?: boolean;
    isExpanded?: boolean;
    children?: any[];
    isIncidentChild?: boolean;
    incidentLabel?: string;

    incidentVolumePer?: number;
    mttrPer?: number;
    responseSLAPer?: number;
    resolutionSLAPer?: number;
    incidentAgingPer?: number;
    label?: string;
    [key: string]: any;

}

const iconMapping: any = {
    'AI Ops': AIIcon,
    'ML Ops': MLIcon,
    'BI Ops': BIOpsIcon,
    'Department Ops': DepartmentIcon,
    'Program': ProgramIcon,
    'Pipeline': DataPipelineSmallIcon,
    'Platform': PlatformCostLogo
}

function ProgramMetrics(props: any) {
    const theme = useTheme();
    const [sectorName, setSectorName] = useState('all');
    const [sectorOptionsList, setSectorOptionsList] = useState<any[]>(SECTOR_OPTIONS);
    const [sectorFilterLoader, setSectorFilterLoader] = useState<boolean>(true);
    const [incidentsChartOptions, SetIncidentsChartOptions] = useState<Record<string, any>>({});
    const [incidentsChartLoader, SetIncidentsChartLoader] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Row[]>([]);
    const [costManagementTableData, setCostManagementTableData] = useState<Row[]>([]);
    const isXOps = true;
    const getInitialMonth = () => {
        return isXOps ? moment().subtract(1, 'month').format('YYYY-MM') : '0M';
    };
    const [customMonth, setCustomMonth] = useState(getInitialMonth());
    const [filterMonth, setFilterMonth] = useState(getInitialMonth());
    const [isYearFilterApplied, setIsYearFilterApplied] = useState(false)
    const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
    const [technology, setTechnology] = useState('all');
    const [incidentVolume, setIncidentVolume] = useState(0);
    const [mttr, setMttr] = useState(0);
    const [responseSLA, setResponseSLA] = useState(0);
    const [resolutionSLA, setResolutionSLA] = useState(0);
    const [overallHappinessIndex, setOverallHappinessIndex] = useState(0);
    const [previousIncidentVolume, setPreviousIncidentVolume] = useState(0);
    const [previousMttr, setPreviousMttr] = useState(0);
    const [previousResponseSLA, setPreviousResponseSLA] = useState(0);
    const [previousResolutionSLA, setPreviousResolutionSLA] = useState(0);
    const [previousOverallHappinessIndex, setPreviousOverallHappinessIndex] = useState(0);
    const [technologyList, setTechnologyList] = useState<any[]>([]);
    const [insights, setInsights] = useState<Insights[]>([]);
    const [loader, setLoader] = useState<boolean>(true);
    const [gridLoader, setGridLoader] = useState<boolean>(true);
    const [chartType, setChartype] = useState('User Incidents');
    const [isClosed, setIsClosed] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const chartTypes = ['User Incidents', 'Automated Incidents', 'Change Requests', 'Service Requests', 'Problem Tickets']
    if (props.metricsType === 'Platform') {
        chartTypes.push('Cost Management Tickets')
    }
    const headerIcon = iconMapping[props.metricsType]
    type ChartType = typeof chartTypes[number];

    const defaultColumnNames = [
        ['Platform', 'Pipeline'].includes(props.metricsType) ? props.metricsType : '',
        props.technologyLabel || 'Technology',
        'Assignment Groups',
        'Incident Volume',
        // 'Availability',
        'Response SLA (%)',
        'Resolution SLA (%)',
        // 'Change Request',
        // 'Service Request',
        'MTTR (Hrs)',
        'Aged Incidents',
        'Happiness Index',
        'Lost Time (Hrs)'
    ];

    const serviceRequestColumnNames = [
        ['Platform', 'Pipeline'].includes(props.metricsType) ? props.metricsType : '',
        props.technologyLabel || 'Technology',
        'Assignment Groups',
        chartType === 'Cost Management Tickets' ? 'Cost Management Tickets' : 'SR Volume',
        'Aged SRs',
        'Happiness Index',
        'Lost Time (Hrs)'
    ];

    const changeRequestColumnNames = [
        ['Platform', 'Pipeline'].includes(props.metricsType) ? props.metricsType : '',
        props.technologyLabel || 'Technology',
        'Assignment Groups',
        'CR Volume',
        'Aged CRs'
    ];

    const problemRequestColumnNames = [
        ['Platform', 'Pipeline'].includes(props.metricsType) ? props.metricsType : '',
        props.technologyLabel || 'Technology',
        'Assignment Groups',
        'Problems Volume',
        'Open Problems',
        'Aged Problems'
    ];

    const poolOrder: any = {
        'Teradata': 1,
        'Modern Cloud': 2,
        'Visualization': 3,
        'B2B Integration & Governance': 4,
        'Data Integration BW Azure Tools': 5
    }

    function toggleSubRow(rowId: string) {
        const rows = chartType === 'Cost Management Tickets' ? costManagementTableData : tableData;
        const rowIndex = rows.findIndex((r: any) => r.id === rowId);
        if (rowIndex === -1) return;

        const row = rows[rowIndex];
        const newRows = [...rows];

        if (!row.isExpanded) {
            let children: Row[] = [];
            if (row.technologies && row.technologies.length > 0) {
                children = row.technologies.map((x: any, index: number) => ({
                    ...x,
                    isChildren: true,
                    id: `${rowId}-tech-${index}`
                }));
            }
            newRows.splice(rowIndex + 1, 0, ...children);
            newRows[rowIndex] = { ...row, isExpanded: true, children };
        } else {
            let rowsToRemove = 0;
            for (let i = rowIndex + 1; i < newRows.length; i++) {
                const potentialChild = newRows[i];
                if (potentialChild.isChildren || potentialChild.isIncidentChild) {
                    rowsToRemove++;
                } else {
                    break;
                }
            }

            if (rowsToRemove > 0) {
                newRows.splice(Number(rowIndex) + 1, rowsToRemove);
            }

            const { children, incidentChildren, ...restOfRow } = row;
            newRows[rowIndex] = {
                ...restOfRow,
                isExpanded: false,
                isIncidentExpanded: false
            };
        }
        if (chartType !== 'Cost Management Tickets')
            setTableData(newRows);
        else
            setCostManagementTableData(newRows);
    }

    function toggleIncidentSubRow(rowId: string) {
        const rows = [...tableData];
        const rowIndex = rows.findIndex((r: any) => r.id === rowId);
        if (rowIndex === -1) return;

        const row = rows[rowIndex];

        // decide which prefix to use based on chart type
        const incidentPrefix = chartType === 'Automated Incidents' ? 'automatedIncidents' : 'userIncidents';

        if (!row.isIncidentExpanded) {
            // Build L0–L3 children dynamically
            const children: Row[] = ['L0', 'L1', 'L2', 'L3']
                .map((level, i) => {
                    return {
                        platform: '',
                        technology: '',
                        label: level,

                        incidentVolume: row[`${incidentPrefix}${level}`],
                        incidentVolumePer: row[`${incidentPrefix}${level}Per`],

                        mttr: row[`mttr${level}`],
                        mttrPer: row[`mttr${level}Per`],

                        responseSLA: row[`responseSLA${level}`],
                        responseSLAPer: row[`responseSLA${level}Per`],

                        resolutionSLA: row[`resolutionSLA${level}`],
                        resolutionSLAPer: row[`resolutionSLA${level}Per`],

                        incidentAging: row[`incidentAging${level}`],
                        incidentAgingPer: row[`incidentAging${level}Per`],

                        assignmentGroupCount: null,
                        availability: null,
                        changeRequest: null,
                        serviceRequest: null,
                        srAging: null,
                        happinessIndexIncident: null,
                        lostTimeIncident: null,

                        isIncidentChild: true,
                        incidentLabel: level,
                        id: `${rowId}-incident-${i}`,
                    } as Row;
                })
                // Filter out empty children
                .filter(c => c.incidentVolume !== undefined && c.incidentVolume !== null && c.incidentVolume > 0);

            // Insert below parent
            rows.splice(rowIndex + 1, 0, ...children);
            rows[rowIndex] = { ...row, isIncidentExpanded: true, incidentChildren: children };
        } else {
            // Collapse logic
            let rowsToRemove = 0;
            for (let i = rowIndex + 1; i < rows.length; i++) {
                if (rows[i].isIncidentChild) rowsToRemove++;
                else break;
            }
            if (rowsToRemove > 0) rows.splice(rowIndex + 1, rowsToRemove);
            rows[rowIndex] = { ...row, isIncidentExpanded: false, incidentChildren: [] };
        }

        setTableData(rows);
    }
    const signedPositiveNumberString = (num: number | string) => Number(num) > 0 ? `+${num}` : num
    const signedNumberString = (num: number | string) => Number(num) > 0 ? `+${num}` : Number(num) < 0 ? `${num}` : '0'
    const percentageNumberString = (num: number | string) => `${num}%`
    const timeHrsNumberString = (num: number | string) => `${num} Hrs`


    const getColumns = () => {
        const columnMap: Array<{ key: string; label: string }> = [
            ...(props.metricsType === 'Pipeline' ? [{ key: 'pipeline', label: 'Pipeline' }] : []),
            ...(props.metricsType === 'Platform' ? [{ key: 'platform', label: 'Platform' }] : []),
            { key: 'technology', label: props.technologyLabel || 'Technology' },
            { key: 'assignmentGroupCount', label: 'Assignment Groups' },
            chartType === 'Automated Incidents' ? { key: 'automatedIncidentVolume', label: 'Incident Volume' }
                : { key: 'incidentVolume', label: 'Incident Volume' },
            ...(props.metricsType === 'Platform' ? [{ key: 'availability', label: 'Availability' }] : []),
            { key: 'responseSLA', label: 'Response SLA' },
            { key: 'resolutionSLA', label: 'Resolution SLA' },
            // { key: 'changeRequest', label: 'Change Request' },
            // { key: 'serviceRequest', label: 'Service Request' },
            { key: 'mttr', label: 'MTTR' },
            { key: 'incidentAging', label: 'Aged Incidents' },
            ...(props.metricsType === 'Platform' ? [{ key: 'agedSRs', label: 'Aged SRs' }] : [{ key: 'srAging', label: 'Aged SRs' }]),
            ...(props.metricsType === 'Platform' ? [{ key: 'costManagementTickets', label: 'Cost Management Tickets' }] : []),
            { key: 'srVolume', label: 'SR Volume' },
            ...(chartType === 'Service Requests' ? [
                { key: 'happinessIndexSR', label: 'Happiness Index' },
                { key: 'happinessIndexSRCoverage', label: 'Happiness Index Coverage' }
            ] : []),
            ...(chartType === 'User Incidents' ? [
                { key: 'happinessIndexIncident', label: 'Happiness Index' },
                { key: 'happinessIndexIncidentCoverage', label: 'Happiness Index Coverage' },
            ] : []),
            chartType === 'Service Requests'
                ? { key: 'lostTimeSR', label: 'Lost Time' }
                : { key: 'lostTimeIncident', label: 'Lost Time' },
            { key: 'crVolume', label: 'CR Volume' },
            { key: 'agedCRs', label: 'Aged CRs' },
            { key: 'problemsVolume', label: 'Problems Volume' },
            { key: 'openProblems', label: 'Open Problems' },
            { key: 'agedProblems', label: 'Aged Problems' }
        ];

        const percentFields = ['responseSLA', 'resolutionSLA', 'happinessIndexIncidentCoverage', 'happinessIndexSRCoverage'];
        if (props.metricsType === 'Platform') {
            percentFields.push('availability')
        }
        const hourFields = ['mttr', 'lostTimeSR', 'lostTimeIncident'];

        return columnMap.map(col => {
            let label = col.label;
            if (percentFields.includes(col.key)) {
                label += ' (%)';
            } else if (hourFields.includes(col.key)) {
                label += ' (Hrs)';
            }
            if (['platform', 'pipeline'].includes(col.key)) {
                return {
                    key: col.key,
                    name: label,
                    resizable: true,
                    width: '230px',
                    formatter: ({ row, isCellSelected }: any) => {
                        const hasChildren = row.technologies && row.technologies.length > 0;
                        return (
                            <>
                                {hasChildren && (
                                    <CellExpanderFormatter
                                        isCellSelected={isCellSelected}
                                        expanded={row.isExpanded === true}
                                        onCellExpand={() => toggleSubRow(row.id)}
                                    />
                                )}
                                <RowTypography style={{ paddingLeft: hasChildren ? '5px' : '25px' }}>{row.platform}</RowTypography>
                            </>
                        );
                    }
                };
            } else if (col.key === 'incidentVolume' || col.key === 'automatedIncidentVolume') {
                return {
                    key: col.key,
                    name: label,
                    resizable: true,
                    width: '230px',
                    formatter: ({ row, isCellSelected }: any) => {
                        if (row.isIncidentChild) {
                            const value = row[col.key];
                            const per = row[`${col.key}Per`];
                            return (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '45px' }}>
                                    <RowTypography>{row.label}</RowTypography>
                                    <RowTypography>
                                        {value} {per ? `(${per}%)` : ''}
                                    </RowTypography>
                                </Box>
                            );
                        }

                        const hasChildren = ['L0', 'L1', 'L2', 'L3'].some(level => {
                            const prefix = chartType === 'Automated Incidents' ? 'automatedIncidents' : 'userIncidents';
                            const val = row[`${prefix}${level}`];
                            return val !== undefined && val !== null && val !== 0;
                        });

                        return (
                            <>
                                {hasChildren && (
                                    <CellExpanderFormatter
                                        isCellSelected={isCellSelected}
                                        expanded={row.isIncidentExpanded === true}
                                        onCellExpand={() => toggleIncidentSubRow(row.id)}
                                    />
                                )}
                                <RowTypography style={{ paddingLeft: hasChildren ? '5px' : '25px' }}>
                                    {row[col.key]}
                                </RowTypography>
                            </>
                        );
                    }
                };
            }
            else if (col.key === 'incidentAging') {
                return {
                    key: col.key,
                    name: label,
                    resizable: true,
                    width: '230px',
                    formatter: ({ row }: any) => {
                        if (row.isMetricsChild) {
                            const value = row[col.key];
                            const per = row[`${col.key}Per`];
                            return (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '45px' }}>
                                    <RowTypography>{row.label}</RowTypography>
                                    <RowTypography>{value} ({per}%)</RowTypography>
                                </Box>
                            );
                        }
                        return <RowTypography>{row[col.key]}</RowTypography>;
                    }
                }
            }
            else if (col.key === 'responseSLA' || col.key === 'resolutionSLA') {
                return {
                    key: col.key,
                    name: label,
                    resizable: true,
                    width: '230px',
                    formatter: ({ row }: any) => {
                        if (row.isMetricsChild) {
                            const value = row[col.key];
                            const per = row[`${col.key}Per`];
                            return (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '45px' }}>
                                    <RowTypography>{row.label}</RowTypography>
                                    <RowTypography>{value} ({per}%)</RowTypography>
                                </Box>
                            );
                        }
                        return <RowTypography>{row[col.key]}%</RowTypography>;
                    }
                };
            } else if (col.key === 'mttr') {
                return {
                    key: col.key,
                    name: label,
                    resizable: true,
                    width: 230,
                    formatter: ({ row }: any) => {
                        if (row.isIncidentChild) {
                            return (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '45px' }}>
                                    <RowTypography>{row.mttr} Hrs ({row.mttrPer}%)</RowTypography>
                                </Box>
                            );
                        }
                        return <RowTypography>{row.mttr} Hrs</RowTypography>;
                    }
                };
            }
            else {
                return {
                    key: col.key,
                    name: label,
                    resizable: true,
                    width: '230px',
                    formatter: ({ row }: any) => {
                        if (row.isIncidentChild) return <RowTypography>{''}</RowTypography>;
                        if (row.isMTTRChild) return <RowTypography>{''}</RowTypography>;
                        let value = row[col.key];
                        if (value === null || value === undefined) return <RowTypography>{''}</RowTypography>;
                        if (['happinessIndexIncident', 'happinessIndexSR'].includes(col.key)) {
                            value = !value ? 'N/A' : signedPositiveNumberString(Math.round(value))
                        }
                        if (percentFields.includes(col.key)) {
                            value = !value ? 'N/A' : `${value}%`;
                        } else if (hourFields.includes(col.key)) {
                            value = !value ? 'N/A' : value > 1 ? `${value} Hrs` : `${value} Hr`;
                        }
                        return <RowTypography>{value}</RowTypography>;
                    }
                }
            }
        })
    };


    const handleSorting = (sortingInfo: any) => { };

    const getMonthLabel = (yearMonth: string) => {
        const isYearFilter = filterMonth?.split('-')?.filter(df => !!df)?.length === 1
        return isYearFilter ? filterMonth : dayjs(`${yearMonth}-01`).format("MMM YYYY");
    };

    const buildIncidentChartOption = (
        incidentsData: any[],
        avgData: any,
        chartType: string
    ): Record<string, any> => {
        const incidents = incidentsData.map((item: any) => item.incidentCount);
        const avgValue = avgData;
        const avg2024Incidents = incidentsData.map(() => avgValue);
        const dates = incidentsData.map((item: any) => {
            const date = getMonthLabel(item.yearMonth);
            return date;
        });

        const allValues = [...incidents, ...avg2024Incidents];
        const maxVal = Math.max(...allValues);
        const minVal = Math.min(...allValues);
        const yMax = Math.ceil(maxVal / 10) * 10;
        const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

        return {
            backgroundColor: '#003771',
            tooltip: {
                trigger: 'axis',
                textStyle: { color: '#A7ADCB' },
                confine: true,
                backgroundColor: '#003771',
                formatter: function (params: any[]) {
                    const date = params[0]?.axisValueLabel;
                    let tooltipContent = `<div><strong>Date:</strong> ${date}</div>`;
                    params.forEach((item) => {
                        tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                    });
                    return tooltipContent;
                }
            },
            grid: { top: '7%' },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates,
                axisLabel: { color: '#A7ADCB' },
                splitLine: { show: false }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                axisLabel: {
                    color: '#A7ADCB',
                    formatter: (value: number) => (value >= 1000 ? `${value / 1000}k` : value)
                },
                splitLine: { show: false }
            },
            series: [
                {
                    name: chartType,
                    type: 'line',
                    data: incidents,
                    symbol: 'circle',
                    color: '#009BD8',
                    lineStyle: { width: 3 },
                    label: { show: true, position: 'top', fontSize: 12, color: '#00F0F0' },
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 1,
                            x2: 0,
                            y2: 0,
                            colorStops: [
                                { offset: 0, color: '#053367' },
                                { offset: 1, color: '#009BD8' }
                            ]
                        }
                    }
                },
                {
                    name: `${moment(filterMonth).year() - 1} Average ${chartType}`,
                    label: { show: true, position: 'top', fontSize: 12, color: '#00F0F0' },
                    type: 'line',
                    data: avg2024Incidents,
                    symbol: 'none',
                    color: '#FFC239',
                    lineStyle: { width: 2 }
                }
            ],
            legend: {
                orient: 'horizontal',
                bottom: '1%',
                left: '2%',
                textStyle: { color: '#A7ADCB' },
                selectedMode: false,
                itemWidth: 10,
                itemHeight: 10,
                icon: 'roundRect'
            }
        };
    };

    const fetchTechnologyList = async () => {
        try {
            SetIncidentsChartLoader(true);
            const res = await fetchMetricsTechnologyList({
                metricsType: props.metricsType,
            });

            if (res.success) {
                const result = res.data.result.map((item: any) => item.technology)
                setTechnologyList(result);
            }
        } catch (error) {
            console.error('Error fetching incident trend data:', error);
        }
    };

    const fetchIncidentsChartData = async (technology: string, chartType: string, sector: string, isClosedDate: boolean, filterMonth: any) => {
        try {
            SetIncidentsChartLoader(true);
            const res = await fetchIncidentTrend({
                metricsType: props.metricsType,
                technology,
                chartType,
                sector,
                isClosedDate,
                dateFilter: filterMonth
            });

            if (res.success) {
                const options = buildIncidentChartOption(res.data.currentYearIncident, res.data.lastYearIncident, chartType);
                SetIncidentsChartOptions(options);
            }
        } catch (error) {
            console.error('Error fetching incident trend data:', error);
        } finally {
            SetIncidentsChartLoader(false);
        }
    };
    const calculateIncidentVolume = (data: Row[]) => {
        const total = data.reduce((sum, current: Row) => {
            return sum + (current.automatedIncidentVolume || 0) + (current.incidentVolume || 0)
        }, 0)
        // setIncidentVolume(total)
        return total
    }
    const calculateMttr = (data: Row[]) => {
        const total = data.reduce((sum, current: Row) => {
            return sum + (Number(current.mttr) || 0)
        }, 0)
        const avg = total / data.length
        // setMttr(total)
        return avg
    }
    const calculateResponseSLA = (data: Row[]) => {
        const total = data.reduce((sum, current: Row) => {
            return sum + (Number(current.responseSLA) || 0)
        }, 0)
        const avg = total / data.length
        // setResponseSLA(avg)
        return avg
    }
    const calculateResolutionSLA = (data: Row[]) => {
        const total = data.reduce((sum, current: Row) => {
            return sum + (Number(current.resolutionSLA) || 0)
        }, 0)
        const avg = total / data.length
        // setResolutionSLA(avg)
        return avg
    }
    const calculateOverallHappinessIndex = (data: Row[]) => {
        if (!data?.length) return 0
        const overallHappinessIndex = data[0]?.overallHappinessIndex
        return Math.round(Number(overallHappinessIndex || '0'))
    }
    const processL123Data = (data: Row[]) => {
        return data.map((row: any) => {
            const newRow = { ...row };
            const isAutomated = chartType === 'Automated Incidents';

            // Set L-level counts based on chart type
            newRow.l0Count = isAutomated ? row.automatedIncidentsL0 : row.userIncidentsL0;
            newRow.l1Count = isAutomated ? row.automatedIncidentsL1 : row.userIncidentsL1;
            newRow.l2Count = isAutomated ? row.automatedIncidentsL2 : row.userIncidentsL2;
            newRow.l3Count = isAutomated ? row.automatedIncidentsL3 : row.userIncidentsL3;

            newRow.l0CountPer = isAutomated ? row.automatedIncidentsL0Per : row.userIncidentsL0Per;
            newRow.l1CountPer = isAutomated ? row.automatedIncidentsL1Per : row.userIncidentsL1Per;
            newRow.l2CountPer = isAutomated ? row.automatedIncidentsL2Per : row.userIncidentsL2Per;
            newRow.l3CountPer = isAutomated ? row.automatedIncidentsL3Per : row.userIncidentsL3Per;

            newRow.mttrl0Count = row.mttrL0 ?? 0;
            newRow.mttrl1Count = row.mttrL1 ?? 0;
            newRow.mttrl2Count = row.mttrL2 ?? 0;
            newRow.mttrl3Count = row.mttrL3 ?? 0;

            newRow.mttrl0CountPer = row.mttrL0Per ?? 0;
            newRow.mttrl1CountPer = row.mttrL1Per ?? 0;
            newRow.mttrl2CountPer = row.mttrL2Per ?? 0;
            newRow.mttrl3CountPer = row.mttrL3Per ?? 0;

            // If it's automated incidents, use the automatedIncidentVolume field
            if (isAutomated) {
                newRow.incidentVolume = row.automatedIncidentVolume;
            }

            // Process the nested technologies array as well
            if (newRow.technologies) {
                newRow.technologies = newRow.technologies.map((techRow: any) => {
                    const newTechRow = { ...techRow };
                    newTechRow.l0Count = isAutomated ? techRow.automatedIncidentsL0 : techRow.userIncidentsL0;
                    newTechRow.l1Count = isAutomated ? techRow.automatedIncidentsL1 : techRow.userIncidentsL1;
                    newTechRow.l2Count = isAutomated ? techRow.automatedIncidentsL2 : techRow.userIncidentsL2;
                    newTechRow.l3Count = isAutomated ? techRow.automatedIncidentsL3 : techRow.userIncidentsL3;

                    newTechRow.l0CountPer = isAutomated ? techRow.automatedIncidentsL0Per : techRow.userIncidentsL0Per;
                    newTechRow.l1CountPer = isAutomated ? techRow.automatedIncidentsL1Per : techRow.userIncidentsL1Per;
                    newTechRow.l2CountPer = isAutomated ? techRow.automatedIncidentsL2Per : techRow.userIncidentsL2Per;
                    newTechRow.l3CountPer = isAutomated ? techRow.automatedIncidentsL3Per : techRow.userIncidentsL3Per;

                    newTechRow.mttrl0Count = techRow.mttrL0;
                    newTechRow.mttrl1Count = techRow.mttrL1;
                    newTechRow.mttrl2Count = techRow.mttrL2;
                    newTechRow.mttrl3Count = techRow.mttrL3;

                    newTechRow.mttrl0CountPer = techRow.mttrL0Per;
                    newTechRow.mttrl1CountPer = techRow.mttrL1Per;
                    newTechRow.mttrl2CountPer = techRow.mttrL2Per;
                    newTechRow.mttrl3CountPer = techRow.mttrL3Per;

                    if (isAutomated) {
                        newTechRow.incidentVolume = techRow.automatedIncidentVolume;
                    }
                    return newTechRow;
                });
            }
            return newRow;
        });
    };
    async function listTableData({ selectedMonth, metricsType, sector, isClosedDate }: any) {
        try {
            setGridLoader(true);
            listTableDataForPreviousMonth({ selectedMonth, metricsType, sector, isClosedDate })
            const res = await fetchTechnologyDataList({ selectedMonth, metricsType, sector, isClosedDate });
            if (res.success) {
                let data: Row[] = [];
                if (['Pipeline', 'Platform'].includes(props.metricsType)) {
                    data = res.data.data.sort((a: Row, b: Row) => poolOrder[a.platform] - poolOrder[b.platform]);
                } else {
                    data = res.data.result;
                }

                const processedData = props.metricsType === 'Platform' ? processL123Data(data) : data.map((row: any) => {
                    const newRow = { ...row };
                    if (chartType === 'User Incidents') {
                        newRow.l0Count = row.userIncidentsL0;
                        newRow.l1Count = row.userIncidentsL1;
                        newRow.l2Count = row.userIncidentsL2;
                        newRow.l3Count = row.userIncidentsL3;

                        newRow.l0CountPer = row.userIncidentsL0Per;
                        newRow.l1CountPer = row.userIncidentsL1Per;
                        newRow.l2CountPer = row.userIncidentsL2Per;
                        newRow.l3CountPer = row.userIncidentsL3Per;
                    } else if (chartType === 'Automated Incidents') {
                        newRow.l0Count = row.automatedIncidentsL0;
                        newRow.l1Count = row.automatedIncidentsL1;
                        newRow.l2Count = row.automatedIncidentsL2;
                        newRow.l3Count = row.automatedIncidentsL3;

                        newRow.l0CountPer = row.automatedIncidentsL0Per;
                        newRow.l1CountPer = row.automatedIncidentsL1Per;
                        newRow.l2CountPer = row.automatedIncidentsL2Per;
                        newRow.l3CountPer = row.automatedIncidentsL3Per;
                    }

                    if (newRow.technologies && Array.isArray(newRow.technologies)) {
                        newRow.technologies = newRow.technologies.map((techRow: any) => {
                            const newTechRow = { ...techRow };
                            if (chartType === 'User Incidents') {
                                newTechRow.l0Count = techRow.userIncidentsL0;
                                newTechRow.l1Count = techRow.userIncidentsL1;
                                newTechRow.l2Count = techRow.userIncidentsL2;
                                newTechRow.l3Count = techRow.userIncidentsL3;

                                newTechRow.l0CountPer = techRow.userIncidentsL0Per;
                                newTechRow.l1CountPer = techRow.userIncidentsL1Per;
                                newTechRow.l2CountPer = techRow.userIncidentsL2Per;
                                newTechRow.l3CountPer = techRow.userIncidentsL3Per;
                            } else if (chartType === 'Automated Incidents') {
                                newTechRow.l0Count = techRow.automatedIncidentsL0;
                                newTechRow.l1Count = techRow.automatedIncidentsL1;
                                newTechRow.l2Count = techRow.automatedIncidentsL2;
                                newTechRow.l3Count = techRow.automatedIncidentsL3;

                                newTechRow.l0CountPer = techRow.automatedIncidentsL0Per;
                                newTechRow.l1CountPer = techRow.automatedIncidentsL1Per;
                                newTechRow.l2CountPer = techRow.automatedIncidentsL2Per;
                                newTechRow.l3CountPer = techRow.automatedIncidentsL3Per;
                            }
                            return newTechRow;
                        });
                    }
                    return newRow;
                });
                setIncidentVolume(calculateIncidentVolume(processedData) || 0)
                setMttr(calculateMttr(processedData) || 0)
                setResolutionSLA(calculateResolutionSLA(processedData) || 0)
                setResponseSLA(calculateResponseSLA(processedData) || 0)
                setOverallHappinessIndex(calculateOverallHappinessIndex(processedData) || 0)
                const dataWithIds = processedData.map((row, index) => ({
                    ...row,
                    id: `${row.platform || row.technology}-${index}`
                }));
                const costManagementData = processedData.filter(
                    item => item.costManagementTickets > 0
                );

                setCostManagementTableData(costManagementData);
                setTableData(dataWithIds.filter((item: { platform: string; }) => item.platform !== 'Cost Management'));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setGridLoader(false);
        }
    }
    const getPreviousMonthString = (selectedMonth: string) => {
        const [yearStr, monthStr] = selectedMonth?.split('-')
        const year = parseInt(yearStr, 10)
        const month = parseInt(monthStr, 10) - 1
        const currentDate = new Date(year, month, 1)
        currentDate.setMonth(currentDate.getMonth() - 1)
        const newYear = currentDate.getFullYear()
        const newMonth = String(currentDate.getMonth() + 1).padStart(2, '0')
        return `${newYear}-${newMonth}`
    }
    async function listTableDataForPreviousMonth({ selectedMonth, metricsType, sector, isClosedDate }: any) {
        try {
            const previousMonth = getPreviousMonthString(selectedMonth)
            const res = await fetchTechnologyDataList({ selectedMonth: previousMonth, metricsType, sector, isClosedDate });
            if (res.success) {
                let data: Row[] = [];
                if (['Pipeline', 'Platform'].includes(props.metricsType)) {
                    data = res.data.data.sort((a: Row, b: Row) => poolOrder[a.platform] - poolOrder[b.platform]);
                } else {
                    data = res.data.result;
                }

                const processedData = props.metricsType === 'Platform' ? processL123Data(data) : data.map((row: any) => {
                    const newRow = { ...row };
                    if (chartType === 'User Incidents') {
                        newRow.l0Count = row.userIncidentsL0;
                        newRow.l1Count = row.userIncidentsL1;
                        newRow.l2Count = row.userIncidentsL2;
                        newRow.l3Count = row.userIncidentsL3;

                        newRow.l0CountPer = row.userIncidentsL0Per;
                        newRow.l1CountPer = row.userIncidentsL1Per;
                        newRow.l2CountPer = row.userIncidentsL2Per;
                        newRow.l3CountPer = row.userIncidentsL3Per;
                    } else if (chartType === 'Automated Incidents') {
                        newRow.l0Count = row.automatedIncidentsL0;
                        newRow.l1Count = row.automatedIncidentsL1;
                        newRow.l2Count = row.automatedIncidentsL2;
                        newRow.l3Count = row.automatedIncidentsL3;

                        newRow.l0CountPer = row.automatedIncidentsL0Per;
                        newRow.l1CountPer = row.automatedIncidentsL1Per;
                        newRow.l2CountPer = row.automatedIncidentsL2Per;
                        newRow.l3CountPer = row.automatedIncidentsL3Per;
                    }

                    if (newRow.technologies && Array.isArray(newRow.technologies)) {
                        newRow.technologies = newRow.technologies.map((techRow: any) => {
                            const newTechRow = { ...techRow };
                            if (chartType === 'User Incidents') {
                                newTechRow.l0Count = techRow.userIncidentsL0;
                                newTechRow.l1Count = techRow.userIncidentsL1;
                                newTechRow.l2Count = techRow.userIncidentsL2;
                                newTechRow.l3Count = techRow.userIncidentsL3;

                                newTechRow.l0CountPer = techRow.userIncidentsL0Per;
                                newTechRow.l1CountPer = techRow.userIncidentsL1Per;
                                newTechRow.l2CountPer = techRow.userIncidentsL2Per;
                                newTechRow.l3CountPer = techRow.userIncidentsL3Per;
                            } else if (chartType === 'Automated Incidents') {
                                newTechRow.l0Count = techRow.automatedIncidentsL0;
                                newTechRow.l1Count = techRow.automatedIncidentsL1;
                                newTechRow.l2Count = techRow.automatedIncidentsL2;
                                newTechRow.l3Count = techRow.automatedIncidentsL3;

                                newTechRow.l0CountPer = techRow.automatedIncidentsL0Per;
                                newTechRow.l1CountPer = techRow.automatedIncidentsL1Per;
                                newTechRow.l2CountPer = techRow.automatedIncidentsL2Per;
                                newTechRow.l3CountPer = techRow.automatedIncidentsL3Per;
                            }
                            return newTechRow;
                        });
                    }
                    return newRow;
                });
                setPreviousIncidentVolume(calculateIncidentVolume(processedData) || 0)
                setPreviousMttr(calculateMttr(processedData) || 0)
                setPreviousResolutionSLA(calculateResolutionSLA(processedData) || 0)
                setPreviousResponseSLA(calculateResponseSLA(processedData) || 0)
                setPreviousOverallHappinessIndex(calculateOverallHappinessIndex(processedData) || 0)
            }
        } catch (error) {
            console.error(error);
        } finally {
            setGridLoader(false);
        }
    }


    const handleTechnologyChange = (selectedTechnology: string) => {
        setTechnology(selectedTechnology);
    };

    const handleSectorChange = (value: string) => {
        localStorage.setItem(`xops${props.metricsType}Sector`, value);
        setSectorName(value);
    };

    const handleChartTypeChange = (selectedChartType: string) => {
        setChartype(selectedChartType);
    }

    const handleDateChange = (selectedDate: string) => {
        setFilterMonth(selectedDate);
    };

    const fetchInsights = async (selectedMonth: string) => {
        setLoader(true);
        try {
            const xopsType = props.xopsType || `${props.metricsType} Metrics`
            const response = await getInsights(xopsType, '', selectedMonth);
            if (response.success) {
                setInsights(response.data.data);
            }
        } catch (error) {
            console.error('Failed to get insights:', error);
        } finally {
            setLoader(false);
        }
    };

    const handleAddInsight = async (newInsight: any) => {
        try {
            setLoader(true);
            const xopsType = props.xopsType || `${props.metricsType} Metrics`
            const response = await addOrUpdateInsights({
                title: newInsight?.title,
                xopsType: xopsType,
                description: newInsight?.description,
                month: filterMonth,
            });
            if (response.success) {
                fetchInsights(filterMonth);
            }
        } catch (error) {
            console.error('Failed to add insight:', error);
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateInsight = async (updatedInsight: any) => {
        try {
            setLoader(true);
            const xopsType = props.xopsType || `${props.metricsType} Metrics`
            const response = await addOrUpdateInsights({
                title: updatedInsight?.title,
                xopsType: xopsType,
                description: updatedInsight?.description,
                insight_id: updatedInsight?.id,
                month: filterMonth,
            });
            if (response.success) {
                fetchInsights(filterMonth);
            }
        } catch (error) {
            console.error('Failed to update insight:', error);
        } finally {
            setLoader(false);
        }
    };

    const handleDeleteInsight = async (insightId: number) => {
        try {
            setLoader(true);
            const response = await deleteInsights(insightId);
            if (response.success) {
                fetchInsights(filterMonth);
            }
        } catch (error) {
            console.error('Failed to delete insight:', error);
        } finally {
            setLoader(false);
        }
    };

    const columnNameMap: Partial<Record<ChartType, string[]>> = {
        "Service Requests": serviceRequestColumnNames,
        "Cost Management Tickets": serviceRequestColumnNames,
        "Change Requests": changeRequestColumnNames,
        "Problem Tickets": problemRequestColumnNames,
    };

    const isDifferenceGreaterThanThreshold = (previous: number | string, current: number | string, thresholdPercentage = 5) => {
        previous = Number(previous)
        current = Number(current)
        const absDiff = Math.abs(current - previous)
        return absDiff > ((thresholdPercentage / 100) * previous)
    }

    // ADDED: Format date and sector for the dynamic title
    const formattedDate = isYearFilterApplied ? filterMonth : dayjs(filterMonth).format('MMMM YYYY');
    const formattedSector = sectorName === 'all' ? 'ALL SECTORS' : sectorName.toUpperCase();
    const calculatePercentageChange = (numCurrent = 0, numPrevious = 0) => {
        if (!numPrevious) {
            return numCurrent === 0 ? 0 : 100; // Or handle as an error
        }
        return Number(((numCurrent - numPrevious) / numPrevious) * 100)?.toFixed(2) || 0;
    }

    const handleOpenClosedFilterChange = (event: any) => {
        const isClosed = event.target.checked;
        setIsClosed(isClosed);
    };

    const handleExportToExcel = () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            // Determine the localStorage key based on metricsType (matches the tableType prop passed to CustomizedDataGrid)
            const tableType = props.metricsType === 'Platform' ? 'Platform' : 'Pipeline';
            const storedPref = localStorage.getItem(`${tableType}_user_col_pref`);
            const storedColumnNames: string[] | null = storedPref ? JSON.parse(storedPref) : null;
            // Fall back to chartType-specific default column names if no user preference is saved
            const fallbackColumnNames = columnNameMap[chartType] || defaultColumnNames;
            const preferredColumnNames: string[] = storedColumnNames?.length
                ? storedColumnNames
                : fallbackColumnNames;

            // Build a name→key lookup from all available columns
            const allColumns = getColumns();
            const nameToKey: Record<string, string> = {};
            allColumns.forEach((col: any) => { nameToKey[col.name] = col.key; });

            const currentData = chartType === 'Cost Management Tickets' ? costManagementTableData : tableData;
            // Flatten: only export parent rows (skip expandable children to avoid duplicates)
            const flatRows = currentData.filter(row => !row.isChildren && !row.isIncidentChild);

            // Build export rows in the user-preferred column order
            const exportData = flatRows.map(row => {
                const exportRow: Record<string, any> = {};
                preferredColumnNames.forEach((colName: string) => {
                    const key = nameToKey[colName];
                    if (key !== undefined) {
                        const val = row[key];
                        exportRow[colName] = val ?? '';
                    }
                });
                return exportRow;
            });

            // Build CSV using the user-preferred column order
            const escapeCsv = (v: any) => `"${String(v ?? '').replaceAll('"', '""')}"`;
            const header = preferredColumnNames.filter(n => nameToKey[n] !== undefined).join(',');
            const rows = exportData.map(r =>
                preferredColumnNames
                    .filter((n: string) => nameToKey[n] !== undefined)
                    .map((n: string) => escapeCsv(r[n]))
                    .join(',')
            );
            const csv = [header, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${props.metricsType || 'Metrics'}_${chartType}_${filterMonth}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const resetStates = () => {
        setTableData([])
        setPreviousIncidentVolume(0)
        setPreviousMttr(0)
        setPreviousResolutionSLA(0)
        setPreviousResponseSLA(0)
        setPreviousOverallHappinessIndex(0)
        setGridLoader(true)
    }

    const location = useLocation()
    useEffect(() => {
        setIsYearFilterApplied(filterMonth?.split('-')?.filter(df => !!df)?.length === 1)
    }, [filterMonth])

    useEffect(() => {
        fetchInsights(filterMonth);
    }, [props.metricsType, props.xopsType, filterMonth]);

    useEffect(() => {
        const storedValue = localStorage.getItem(`xops${props.metricsType}Sector`) ?? "all";
        setSectorName(storedValue);
        fetchTechnologyList();
    }, [props.metricsType]);

    useEffect(() => {
        fetchIncidentsChartData(technology, chartType, sectorName, isClosed, filterMonth);
        listTableData({ selectedMonth: filterMonth, metricsType: props.metricsType, sector: sectorName, isClosedDate: isClosed });
    }, [chartType, technology, filterMonth, sectorName, props.metricsType, isClosed]);

    return (
        <Box
            sx={{
                mt: { xs: '70px', sm: '50px', md: '60px' },
                backgroundColor: 'backgroundLight.primary',
            }}
        >
            {/* UPDATED: Title prop now includes formattedSector and formattedDate */}
            <PageHeader title={`${props.metricsType} Metrics - ${formattedSector} | ${formattedDate}`} icon={headerIcon}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontSize='16px' fontWeight={600} color={theme.palette.textLight.dark}>Metrics by Open Date</Typography>
                    <Switch
                        checked={isClosed}
                        onChange={handleOpenClosedFilterChange}
                        size="small"
                        sx={{
                            width: 44, height: 22, padding: 0,
                            '& .MuiSwitch-switchBase': {
                                '&.Mui-checked': {
                                    transform: 'translateX(22px)',
                                    '& + .MuiSwitch-track': { backgroundColor: '#71758F', opacity: 1, },
                                    '& .MuiSwitch-thumb': { backgroundColor: 'white.main', },
                                },
                            },
                            '& .MuiSwitch-thumb': { width: 14, height: 14, backgroundColor: 'backgroundLight.dark', boxShadow: 'none', },
                            '& .MuiSwitch-track': { borderRadius: 14, backgroundColor: '#71758F', opacity: 1, },
                        }}
                    />
                    <Typography fontSize='16px' fontWeight={600} color={theme.palette.textLight.dark}>Metrics by Closed Date</Typography>
                </Box>
                {!!props.showSectorFilter && <SectorFilter
                    sectorName={sectorName}
                    setSectorName={setSectorName}
                    sectorOptionsList={sectorOptionsList}
                    handleChange={handleSectorChange}
                    hasNotProvided={true}
                />}
                <YearMonthPicker
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                />
                {/* <DateFilterDropdown
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                    disableFilter={false}
                    isXOps={true}
                /> */}
            </PageHeader>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-end', md: 'flex-start' }, gap: 1, mt: '1rem', px: { xs: 1, md: 2, lg: 3 } }}>
            <Grid container spacing={1} sx={{ flex: 1 }}>
                <Grid item xs={12} md={12 / 5}>
                    <NumberWidget
                        isAnnualMetrics={isYearFilterApplied}
                        title={<HighlightedText text="Incident Volume" />}
                        number={incidentVolume || 0}
                        change={calculatePercentageChange(incidentVolume, previousIncidentVolume) || 0}
                        changeType={incidentVolume - previousIncidentVolume >= 0 ? 'increase' : 'decrease'}
                        tooltipLabel=""
                        // isHappySignalTooltip={true}
                        isLoading={gridLoader}
                        footer={{ footerLink: '#', footerLabel: 'View Details' }}
                        showChangePercentage={!gridLoader && true}
                        reverseTrendColor={true}
                        bgColor={
                            !gridLoader && (incidentVolume - previousIncidentVolume > 0) &&
                                isDifferenceGreaterThanThreshold(previousIncidentVolume, incidentVolume)
                                ? 'boxColor.orangeDark.bgColor'
                                : 'primary.main'
                        }
                    />
                </Grid>
                <Grid item xs={12} md={12 / 5}>
                    <NumberWidget
                        isAnnualMetrics={isYearFilterApplied}
                        title={<HighlightedText text="MTTR" />}
                        number={timeHrsNumberString(Number(mttr)?.toFixed(2) || 0)}
                        change={calculatePercentageChange(mttr, previousMttr) || 0}
                        changeType={mttr - previousMttr >= 0 ? 'increase' : 'decrease'}
                        tooltipLabel=""
                        // isHappySignalTooltip={true}
                        isLoading={gridLoader}
                        footer={{ footerLink: '#', footerLabel: 'View Details' }}
                        showChangePercentage={!gridLoader && true}
                        reverseTrendColor={true}
                        bgColor={
                            !gridLoader && (mttr - previousMttr > 0) &&
                                isDifferenceGreaterThanThreshold(previousMttr, mttr)
                                ? 'boxColor.orangeDark.bgColor'
                                : 'primary.main'
                        }
                    />
                </Grid>
                <Grid item xs={12} md={12 / 5}>
                    <NumberWidget
                        isAnnualMetrics={isYearFilterApplied}
                        title={<HighlightedText text="Response SLA" />}
                        number={percentageNumberString(Number(responseSLA)?.toFixed(2) || 0)}
                        change={calculatePercentageChange(responseSLA, previousResponseSLA) || 0}
                        changeType={responseSLA - previousResponseSLA >= 0 ? 'increase' : 'decrease'}
                        tooltipLabel=""
                        // isHappySignalTooltip={true}
                        isLoading={gridLoader}
                        footer={{ footerLink: '#', footerLabel: 'View Details' }}
                        showChangePercentage={!gridLoader && true}
                        bgColor={
                            !gridLoader && (responseSLA - previousResponseSLA < 0) &&
                                isDifferenceGreaterThanThreshold(previousResponseSLA, responseSLA)
                                ? 'boxColor.orangeDark.bgColor'
                                : 'primary.main'
                        }
                    />
                </Grid>
                <Grid item xs={12} md={12 / 5}>
                    <NumberWidget
                        isAnnualMetrics={isYearFilterApplied}
                        title={<HighlightedText text="Resolution SLA" />}
                        number={percentageNumberString(Number(resolutionSLA)?.toFixed(2) || 0)}
                        change={calculatePercentageChange(resolutionSLA, previousResolutionSLA) || 0}
                        changeType={resolutionSLA - previousResolutionSLA >= 0 ? 'increase' : 'decrease'}
                        tooltipLabel=""
                        // isHappySignalTooltip={true}
                        isLoading={gridLoader}
                        footer={{ footerLink: '#', footerLabel: 'View Details' }}
                        showChangePercentage={!gridLoader && true}
                        bgColor={
                            !gridLoader && (resolutionSLA - previousResolutionSLA < 0) &&
                                isDifferenceGreaterThanThreshold(previousResolutionSLA, resolutionSLA)
                                ? 'boxColor.orangeDark.bgColor'
                                : 'primary.main'
                        }
                    />
                </Grid>
                <Grid item xs={12} md={12 / 5}>
                    <NumberWidget
                        isAnnualMetrics={isYearFilterApplied}
                        title={<HighlightedText text="Happiness Index" />}
                        number={signedNumberString(Number(overallHappinessIndex) || 0)}
                        change={calculatePercentageChange(overallHappinessIndex, previousOverallHappinessIndex) || 0}
                        changeType={overallHappinessIndex - previousOverallHappinessIndex >= 0 ? 'increase' : 'decrease'}
                        tooltipLabel=""
                        // isHappySignalTooltip={true}
                        isLoading={gridLoader}
                        footer={{ footerLink: '#', footerLabel: 'View Details' }}
                        showChangePercentage={!gridLoader && true}
                        bgColor={
                            !gridLoader && (overallHappinessIndex - previousOverallHappinessIndex < 0) &&
                                isDifferenceGreaterThanThreshold(previousOverallHappinessIndex, overallHappinessIndex)
                                ? 'boxColor.orangeDark.bgColor'
                                : 'primary.main'
                        }
                    />
                </Grid>
            </Grid>
            <CaptureExportControls
                onScreenshot={() => {}}
                screenCapturing={false}
                onExport={handleExportToExcel}
                isExporting={isExporting}
                isVisible={true}
                showExport={true}
                type="snow"
                exportLabel="Export to Excel"
                sx={{
                    ml: 0,
                    alignSelf: 'flex-end',
                    zIndex: 1,
                    '& #exclude-camera': {
                        display: 'none',
                    },
                }}
            />
            </Box>
            <Grid container spacing={1} marginTop="1rem" px={{ xs: 1, md: 2, lg: 3 }}>
                {/* 1. LAYOUT FIX: Set sm={4} for the graph */}
                <Grid item xs={12} sm={4} md={4} lg={4}>
                    <MultiLineWidget
                        title=""
                        isLoading={incidentsChartLoader}
                        data={incidentsChartOptions}
                        hasFilter={true}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <CustomDropdown
                                handleChange={(event) => {
                                    setChartype(event.target.value)
                                    handleChartTypeChange(event.target.value)
                                }}
                                value={chartType}
                                options={chartTypes.map((item: string) => ({
                                    label: item,
                                    value: item
                                }))}
                                boxProps={{
                                    sx: { minWidth: '20px', position: 'static' }
                                }}
                            />
                            <IncidentFilter
                                sectorName={technology}
                                setSectorName={setTechnology}
                                sectorOptionsList={technologyList}
                                handleChange={handleTechnologyChange}
                                type={props.chartLabel}
                                disabled={false}
                                boxProps={{
                                    sx: { minWidth: '20px', position: 'static' }
                                }}
                            />
                        </Box>
                    </MultiLineWidget>
                </Grid>
                {/* 2. LAYOUT FIX: Set sm={8} for the table */}
                <Grid item xs={12} sm={8} md={8} lg={8}>
                    <Box sx={{ backgroundColor: 'rgba(0, 55, 113, 0.2)', borderRadius: '5px', p: 2, minHeight: '340px' }}>
                        {gridLoader ? (
                            <LoaderComponent pt={16} />
                        ) : (
                            <CustomizedDataGrid
                                title={<HighlightedText text={`Top ${props.chartLabel}`} />}
                                tableColumns={getColumns()}
                                tableData={chartType == 'Cost Management Tickets' ? costManagementTableData : tableData}
                                tableType={props.metricsType === 'Platform' ? "Platform" : "Pipeline"}
                                defaultColumnNames={columnNameMap[chartType] || defaultColumnNames}
                                onSorting={handleSorting}
                                currSorting={sortingInfo}
                                listLength={chartType == 'Cost Management Tickets' ? costManagementTableData.length : tableData.length}
                                isDateRangeFilter={true}
                                isDisabled={false}
                                isRenderPagination={false}
                                isShowMoreButton={true}
                            >
                            </CustomizedDataGrid>
                        )}
                    </Box>
                </Grid>
                {/* 3. STRUCTURAL FIX: Ensured Insights is directly under the main container grid */}
                <Grid item xs={12} sx={{ mb: 3 }}>
                    <Box marginTop={4} marginBottom={5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px' }}>
                            <Typography variant="subHeader" sx={{ color: '#009BDB', mr: { xs: "1rem", sm: '2rem' } }}>{<HighlightedText text="Insights/Actions" />}</Typography>
                            <Divider sx={{ flexGrow: 1, backgroundColor: '#009BDB', marginLeft: 2 }} />
                        </Box>
                        <Typography
                            variant="bodyText"
                            sx={{ fontStyle: 'italic', color: '#71758F' }}
                        >
                            You can display and update Insights/Actions if any.
                        </Typography>
                    </Box>
                    {loader ? (
                        <LoaderComponent />
                    ) : (
                        <Insights
                            insights={insights}
                            setInsights={setInsights}
                            onAdd={handleAddInsight}
                            onUpdate={handleUpdateInsight}
                            onDelete={handleDeleteInsight}
                        />
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}

export default ProgramMetrics